import html2canvas from 'html2canvas';
import { useCallback, useState } from 'react';

import { type AttachmentFile } from '../types';

export interface UseScreenshotResult {
  autoScreenshot: Blob | null;
  autoScreenshotUrl: string | null;
  isCapturing: boolean;
  extraFiles: AttachmentFile[];
  captureScreen: () => Promise<void>;
  removeAutoScreenshot: () => void;
  addExtraFiles: (files: File[]) => void;
  removeExtraFile: (index: number) => void;
  clearAttachments: () => void;
}

export const useScreenshot = (): UseScreenshotResult => {
  const [autoScreenshot, setAutoScreenshot] = useState<Blob | null>(null);
  const [autoScreenshotUrl, setAutoScreenshotUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [extraFiles, setExtraFiles] = useState<AttachmentFile[]>([]);

  const takeCanvasScreenshot = async (): Promise<Blob> => {
    try {
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: false,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        logging: false,
        ignoreElements: (el) => {
          // Exclude known non-CORS external CDNs from direct canvas taint
          if (el.tagName === 'IMG') {
            const src = (el as HTMLImageElement).src || '';
            return src.includes('digitaloceanspaces.com');
          }
          return false;
        },
      });

      return await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create blob from canvas'));
        }, 'image/png');
      });
    } catch {
      const fallbackCanvas = await html2canvas(document.documentElement, {
        useCORS: false,
        allowTaint: true,
        scale: 1,
        logging: false,
        ignoreElements: (el) => el.tagName === 'IMG',
      });

      return await new Promise<Blob>((resolve, reject) => {
        fallbackCanvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Fallback canvas failed'));
        }, 'image/png');
      });
    }
  };

  const captureScreen = useCallback(async () => {
    setIsCapturing(true);
    try {
      const blob = await takeCanvasScreenshot();
      setAutoScreenshot(blob);
      setAutoScreenshotUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.warn('useScreenshot: Capture failed', err);
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const removeAutoScreenshot = useCallback(() => {
    if (autoScreenshotUrl) {
      URL.revokeObjectURL(autoScreenshotUrl);
    }
    setAutoScreenshot(null);
    setAutoScreenshotUrl(null);
  }, [autoScreenshotUrl]);

  const addExtraFiles = useCallback(
    (files: File[]) => {
      const availableSlots = 3 - extraFiles.length;
      const filesToAdd = files.slice(0, availableSlots);

      const newAttachments = filesToAdd.map((file) => ({
        file,
        previewUrl: URL.createObjectURL(file),
      }));

      setExtraFiles((prev) => [...prev, ...newAttachments]);
    },
    [extraFiles.length],
  );

  const removeExtraFile = useCallback((index: number) => {
    setExtraFiles((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    removeAutoScreenshot();
    extraFiles.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
    setExtraFiles([]);
  }, [removeAutoScreenshot, extraFiles]);

  return {
    autoScreenshot,
    autoScreenshotUrl,
    isCapturing,
    extraFiles,
    captureScreen,
    removeAutoScreenshot,
    addExtraFiles,
    removeExtraFile,
    clearAttachments,
  };
};
